import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatRupiah, formatDateDisplay } from '../utils/helpers';
import { toast } from 'sonner';
import { Plus, Search, Download, Pencil, Trash2, X, Upload, FileDown } from 'lucide-react';

const Pengeluaran = () => {
  const { pengeluaran, cabang, fetchPengeluaran, fetchInventori, fetchSummary, fetchChartData } = useApp();
  const { canManageTransactions, hasGlobalAccess } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCabang, setFilterCabang] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [importing, setImporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const fileInputRef = useRef(null);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('tanggal');
  const [sortDir, setSortDir] = useState('asc');
  const [formData, setFormData] = useState({
    tanggal: '',
    nama_barang: '',
    jumlah_unit: '1',
    harga_satuan: '',
    harga_total: '',
    keterangan: '',
    cabang_id: null
  });

  const resetForm = () => {
    setFormData({
      tanggal: '',
      nama_barang: '',
      jumlah_unit: '1',
      harga_satuan: '',
      harga_total: '',
      keterangan: '',
      cabang_id: null
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (!canManageTransactions) {
      toast.error('Role ini hanya memiliki akses baca pada data pengeluaran');
      return;
    }
    if (item) {
      setEditingItem(item);
      setFormData({
        tanggal: item.tanggal,
        nama_barang: item.nama_barang,
        jumlah_unit: item.jumlah_unit.toString(),
        harga_satuan: item.harga_satuan.toString(),
        harga_total: item.harga_total.toString(),
        keterangan: item.keterangan || '',
        cabang_id: item.cabang_id || null
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const calculateTotal = (jumlah, hargaSatuan) => {
    const qty = parseInt(jumlah) || 0;
    const price = parseFloat(hargaSatuan) || 0;
    return qty * price;
  };

  const handleJumlahChange = (value) => {
    const newTotal = calculateTotal(value, formData.harga_satuan);
    setFormData({ ...formData, jumlah_unit: value, harga_total: newTotal.toString() });
  };

  const handleHargaSatuanChange = (value) => {
    const newTotal = calculateTotal(formData.jumlah_unit, value);
    setFormData({ ...formData, harga_satuan: value, harga_total: newTotal.toString() });
  };

  const handleSubmit = async () => {
    if (!canManageTransactions) {
      toast.error('Akses ditolak untuk mengubah data pengeluaran');
      return;
    }
    try {
      const data = {
        ...formData,
        jumlah_unit: parseInt(formData.jumlah_unit) || 1,
        harga_satuan: parseFloat(formData.harga_satuan) || 0,
        harga_total: parseFloat(formData.harga_total) || 0,
        cabang_id: formData.cabang_id === 'global' ? null : formData.cabang_id
      };

      if (editingItem) {
        await api.updatePengeluaran(editingItem.id, data);
        toast.success('Pengeluaran berhasil diperbarui');
      } else {
        await api.createPengeluaran(data);
        toast.success('Pengeluaran berhasil ditambahkan (otomatis masuk inventori)');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchPengeluaran();
      fetchInventori();
      fetchSummary();
      fetchChartData();
    } catch (error) {
      toast.error('Gagal menyimpan data');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!canManageTransactions) {
      toast.error('Akses ditolak untuk menghapus data pengeluaran');
      return;
    }
    if (window.confirm('Yakin ingin menghapus data ini? Inventori terkait juga akan dihapus.')) {
      try {
        await api.deletePengeluaran(id);
        toast.success('Pengeluaran berhasil dihapus');
        fetchPengeluaran();
        fetchInventori();
        fetchSummary();
        fetchChartData();
      } catch (error) {
        toast.error('Gagal menghapus data');
      }
    }
  };

  const handleImport = async (e) => {
    if (!canManageTransactions) {
      toast.error('Akses ditolak untuk import data pengeluaran');
      return;
    }
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const res = await api.importPengeluaran(file);
      toast.success(res.data.message);
      if (res.data.errors && res.data.errors.length > 0) {
        res.data.errors.forEach(err => toast.error(err));
      }
      setIsImportDialogOpen(false);
      fetchPengeluaran();
      fetchInventori();
      fetchSummary();
      fetchChartData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal import data');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFilter = () => {
    const params = {};
    setCurrentPage(1);
    if (filterCabang && filterCabang !== 'all') params.cabang_id = filterCabang;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (search) params.search = search;
    fetchPengeluaran(params);
  };

  useEffect(() => {
    handleFilter();
  }, [filterCabang, startDate, endDate]);

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilter();
  };

  const clearFilters = () => {
    setSearch('');
    setFilterCabang('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchPengeluaran();
  };

  const getCabangName = (cabangId) => {
    if (!cabangId) return 'Global';
    const found = cabang.find(c => c.id === cabangId);
    return found ? found.nama : 'Global';
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const getSortValue = (item, key) => {
    switch (key) {
      case 'tanggal': {
        const raw = (item.tanggal || '').trim();
        const parts = raw.split('/');
        if (parts.length === 3) {
          const d = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10);
          const y = parseInt(parts[2], 10);
          if (!Number.isNaN(d) && !Number.isNaN(m) && !Number.isNaN(y)) {
            return new Date(y, m - 1, d).getTime();
          }
        }
        const t = Date.parse(raw);
        return Number.isNaN(t) ? 0 : t;
      }
      case 'nama_barang':
        return (item.nama_barang || '').toLowerCase();
      case 'jumlah_unit':
        return item.jumlah_unit || 0;
      case 'harga_satuan':
        return item.harga_satuan || 0;
      case 'harga_total':
        return item.harga_total || 0;
      case 'cabang':
        return (getCabangName(item.cabang_id) || '').toLowerCase();
      case 'keterangan':
        return (item.keterangan || '').toLowerCase();
      default:
        return '';
    }
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return null;
    return <span className="ml-1 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortedPengeluaran = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...pengeluaran].sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [pengeluaran, sortKey, sortDir, cabang]);

  const totalPengeluaran = pengeluaran.reduce((sum, p) => sum + p.harga_total, 0);

  const totalPages = Math.max(1, Math.ceil(sortedPengeluaran.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pagedPengeluaran = sortedPengeluaran.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-6" data-testid="pengeluaran-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Pengeluaran</h1>
          <p className="text-muted-foreground text-sm mt-1">Kelola semua pengeluaran (otomatis masuk inventori)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canManageTransactions && (
            <Button variant="outline" size="sm" onClick={() => setIsImportDialogOpen(true)} data-testid="import-pengeluaran-btn">
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Select value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger className="w-[90px]" data-testid="export-format-pengeluaran">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">Excel</SelectItem>
              </SelectContent>
            </Select>
            <a href={api.exportPengeluaran(filterCabang !== 'all' ? filterCabang : null, exportFormat)} target="_blank" rel="noreferrer">
              <Button variant="secondary" size="sm" data-testid="export-pengeluaran-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </a>
          </div>
          {canManageTransactions && (
            <Button onClick={() => handleOpenDialog()} data-testid="add-pengeluaran-btn">
              <Plus className="h-4 w-4 mr-2" />
              Tambah
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cari nama barang atau keterangan..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-pengeluaran"
                />
              </div>
              <Button type="submit" variant="secondary" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </form>
            <Select value={filterCabang} onValueChange={setFilterCabang}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-cabang-pengeluaran">
                <SelectValue placeholder="Semua Cabang" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Cabang</SelectItem>
                {hasGlobalAccess && <SelectItem value="global">Global</SelectItem>}
                {cabang.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full md:w-[150px]"
              data-testid="start-date-pengeluaran"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full md:w-[150px]"
              data-testid="end-date-pengeluaran"
            />
            {(search || filterCabang !== 'all' || startDate || endDate) && (
              <Button variant="ghost" size="icon" onClick={clearFilters}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-rose-50 border-rose-200">
        <CardContent className="p-4">
          <p className="text-xs font-semibold uppercase text-rose-700">Total Pengeluaran</p>
          <p className="text-2xl font-black text-rose-600">{formatRupiah(totalPengeluaran)}</p>
          <p className="text-xs text-rose-600 mt-1">{pengeluaran.length} transaksi</p>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-border/60">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Tampilkan</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v, 10)); setCurrentPage(1); }}>
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">data</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {sortedPengeluaran.length === 0 ? 0 : startIndex + 1} - {Math.min(startIndex + pageSize, sortedPengeluaran.length)} dari {sortedPengeluaran.length}
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="w-[50px]">No</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => handleSort('tanggal')}>Tanggal {renderSortIcon('tanggal')}</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => handleSort('nama_barang')}>Nama Barang {renderSortIcon('nama_barang')}</TableHead>
                  <TableHead className="text-right cursor-pointer select-none" onClick={() => handleSort('jumlah_unit')}>Qty {renderSortIcon('jumlah_unit')}</TableHead>
                  <TableHead className="text-right cursor-pointer select-none" onClick={() => handleSort('harga_satuan')}>Harga Satuan {renderSortIcon('harga_satuan')}</TableHead>
                  <TableHead className="text-right cursor-pointer select-none" onClick={() => handleSort('harga_total')}>Total {renderSortIcon('harga_total')}</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => handleSort('cabang')}>Cabang {renderSortIcon('cabang')}</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => handleSort('keterangan')}>Keterangan {renderSortIcon('keterangan')}</TableHead>
                  <TableHead className="w-[100px] text-center">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedPengeluaran.length > 0 ? pagedPengeluaran.map((item, index) => (
                  <TableRow key={item.id} className="hover:bg-zinc-50/50">
                    <TableCell className="font-mono text-muted-foreground">{startIndex + index + 1}</TableCell>
                    <TableCell className="font-mono text-sm">{formatDateDisplay(item.tanggal)}</TableCell>
                    <TableCell className="font-medium">{item.nama_barang}</TableCell>
                    <TableCell className="text-right font-mono">{item.jumlah_unit}</TableCell>
                    <TableCell className="text-right font-mono">{formatRupiah(item.harga_satuan)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-rose-600">
                      {formatRupiah(item.harga_total)}
                    </TableCell>
                    <TableCell>{getCabangName(item.cabang_id)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">
                      {item.keterangan || '-'}
                    </TableCell>
                    <TableCell>
                      {canManageTransactions ? (
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(item)}
                            data-testid={`edit-pengeluaran-${item.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(item.id)}
                            className="text-rose-500 hover:text-rose-600"
                            data-testid={`delete-pengeluaran-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center text-xs text-muted-foreground">Read only</div>
                      )}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Belum ada data pengeluaran
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end gap-2 p-4 border-t">
            <span className="text-sm text-muted-foreground">Hal {currentPage} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              Next
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Form */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingItem ? 'Edit Pengeluaran' : 'Tambah Pengeluaran'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Form untuk {editingItem ? 'mengedit' : 'menambah'} data pengeluaran
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tanggal</Label>
                <Input
                  type="date"
                  value={formData.tanggal}
                  onChange={(e) => setFormData({ ...formData, tanggal: e.target.value })}
                  data-testid="input-tanggal-pengeluaran"
                />
              </div>
              <div className="space-y-2">
                <Label>Cabang</Label>
                <Select 
                  value={formData.cabang_id || 'global'} 
                  onValueChange={(v) => setFormData({ ...formData, cabang_id: v === 'global' ? null : v })}
                >
                  <SelectTrigger data-testid="select-cabang-pengeluaran">
                    <SelectValue placeholder="Pilih Cabang" />
                  </SelectTrigger>
                  <SelectContent>
                    {hasGlobalAccess && <SelectItem value="global">Global</SelectItem>}
                    {cabang.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nama Barang / Jasa</Label>
              <Input
                value={formData.nama_barang}
                onChange={(e) => setFormData({ ...formData, nama_barang: e.target.value })}
                placeholder="Nama barang atau jasa"
                data-testid="input-nama-barang"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Jumlah</Label>
                <Input
                  type="number"
                  value={formData.jumlah_unit}
                  onChange={(e) => handleJumlahChange(e.target.value)}
                  min="1"
                  data-testid="input-jumlah-unit"
                />
              </div>
              <div className="space-y-2">
                <Label>Harga Satuan</Label>
                <Input
                  type="number"
                  value={formData.harga_satuan}
                  onChange={(e) => handleHargaSatuanChange(e.target.value)}
                  placeholder="0"
                  data-testid="input-harga-satuan"
                />
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <Input
                  type="number"
                  value={formData.harga_total}
                  onChange={(e) => setFormData({ ...formData, harga_total: e.target.value })}
                  placeholder="0"
                  className="bg-zinc-50"
                  data-testid="input-harga-total"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Keterangan</Label>
              <Input
                value={formData.keterangan}
                onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                placeholder="Keterangan (opsional)"
                data-testid="input-keterangan-pengeluaran"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleSubmit} data-testid="submit-pengeluaran-btn">
              {editingItem ? 'Simpan' : 'Tambah'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Import Pengeluaran dari CSV</DialogTitle>
            <DialogDescription>
              Upload file CSV untuk import data pengeluaran secara massal (otomatis masuk inventori)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-zinc-50 rounded-sm space-y-3">
              <p className="text-sm font-medium">Langkah-langkah:</p>
              <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                <li>Download template CSV terlebih dahulu</li>
                <li>Isi data sesuai format template</li>
                <li>Upload file CSV yang sudah diisi</li>
              </ol>
            </div>
            <div className="space-y-2">
              <Label>1. Download Template</Label>
                <Button 
                variant="outline" 
                className="w-full" 
                data-testid="download-template-pengeluaran"
                onClick={() => window.open(api.templatePengeluaran(), '_blank')}
              >
                <FileDown className="h-4 w-4 mr-2" />
                Download Template CSV
              </Button>
            </div>
            <div className="space-y-2">
              <Label>2. Upload File CSV</Label>
              <Input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleImport}
                disabled={importing}
                data-testid="upload-csv-pengeluaran"
              />
              {importing && <p className="text-sm text-muted-foreground">Mengimport data...</p>}
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
              <p className="text-xs text-amber-700">
                <strong>Format kolom CSV:</strong> tanggal, nama_barang, jumlah_unit, harga_satuan, keterangan, cabang_nama
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsImportDialogOpen(false)}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pengeluaran;
